/**
 * Global English → Chinese runtime translator for toast / alert / confirm.
 *
 * Rationale: many pages hard-code English strings in `toast.success(...)`,
 * `alert(...)`, `window.confirm(...)`, or surface backend error strings
 * verbatim. Rather than edit every file, we install a dictionary-backed
 * translator once at startup that:
 *
 *   1. Looks up common exact matches in the dictionary below.
 *   2. Normalises trailing punctuation / whitespace before lookup.
 *   3. Falls back to the original string if no mapping exists (so unknown
 *      messages still render — we just never hide them).
 *
 * Language is read from localStorage to avoid a React-tree dependency; the
 * LanguageContext already persists `language` there. When not `zh`, the
 * translator short-circuits and passes through.
 */

import { toast } from 'react-toastify';

// ───────────────────────────────────────────────────────────────
//  Dictionary
// ───────────────────────────────────────────────────────────────

// Add lowercased-trimmed English → Chinese. Lookup is case-insensitive and
// trims both trailing punctuation (. ! ?) and leading/trailing whitespace.
const DICT = {
  // Generic
  'operation successful': '操作成功',
  'operation completed': '操作成功',
  'success': '成功',
  'saved': '已保存',
  'saved successfully': '保存成功',
  'updated successfully': '更新成功',
  'created successfully': '创建成功',
  'deleted successfully': '删除成功',
  'deleted': '已删除',
  'created': '已创建',
  'updated': '已更新',
  'something went wrong': '出错了',
  'an error occurred': '发生错误',
  'error': '错误',
  'unauthorized': '未授权',
  'forbidden': '无权限',
  'not found': '未找到',
  'server error': '服务器错误',
  'network error': '网络错误',
  'loading': '加载中',
  'loading...': '加载中...',
  'please try again': '请重试',
  'please try again later': '请稍后重试',
  'are you sure': '确定吗',
  'are you sure?': '确定吗？',
  'yes': '是',
  'no': '否',
  'ok': '确定',
  'cancel': '取消',
  'confirm': '确认',
  'copied to clipboard': '已复制到剪贴板',
  'copied to clipboard!': '已复制到剪贴板！',
  'payment link copied': '支付链接已复制',
  'payment link copied!': '支付链接已复制！',

  // Profiles
  'profile not found': '未找到档案',
  'profile created successfully': '档案创建成功',
  'profile updated successfully': '档案更新成功',
  'profile deleted successfully': '档案删除成功',
  'profile approved successfully': '档案审核通过',
  'profile rejected': '档案已拒绝',
  'profile flagged for review': '档案已标记待审核',
  'failed to load profile': '加载档案失败',
  'failed to load profiles': '加载档案列表失败',
  'failed to delete profile': '删除档案失败',
  'failed to create profile': '创建档案失败',
  'failed to update profile': '更新档案失败',
  'failed to get profile': '获取档案失败',
  'failed to get profiles': '获取档案列表失败',
  'failed to fetch profiles': '加载档案失败',
  'failed to get profile types': '获取档案类型失败',
  'are you sure you want to delete this profile?': '确定要删除此档案吗？',
  'please select profiles to delete': '请选择要删除的档案',
  'please select profiles to assign': '请选择要分配的档案',
  'please select profiles to unassign': '请选择要取消分配的档案',
  'please fill in required fields (first name, last name, email)': '请填写必填字段（名、姓、邮箱）',
  'profile photo must be less than 5mb': '头像必须小于 5MB',
  'photo must be less than 5mb': '图片必须小于 5MB',
  'file size must be less than 5mb': '文件大小不能超过 5MB',
  'maximum 10 gallery photos allowed': '最多允许 10 张相册照片',
  'please select an image file': '请选择图片文件',
  'please agree to the contract terms': '请先同意合同条款',

  // Matches
  'match created successfully': '匹配创建成功',
  'match created successfully!': '匹配创建成功！',
  'match deleted successfully': '匹配删除成功',
  'match request sent successfully': '匹配请求已发送',
  'match request sent successfully!': '匹配请求已发送！',
  'failed to delete match': '删除匹配失败',
  'failed to update match': '更新匹配失败',
  'failed to update match status': '更新匹配状态失败',
  'failed to load match details': '加载匹配详情失败',
  'please select a client': '请选择客户',
  'please select a profile to match with': '请选择要匹配的档案',
  'please select two profiles to match': '请选择两个档案进行匹配',
  'are you sure you want to delete this match?': '确定要删除此匹配吗？',
  'notes saved': '备注已保存',
  'notes saved successfully': '备注保存成功',
  'failed to save notes': '备注保存失败',

  // Invoices / Billing
  'invoice not found': '未找到发票',
  'invoice already paid': '发票已支付',
  'invoice created successfully': '发票创建成功',
  'invoice created successfully!': '发票创建成功！',
  'invoice updated successfully': '发票更新成功',
  'failed to create invoice': '创建发票失败',
  'failed to update invoice': '更新发票失败',
  'failed to get invoice': '获取发票失败',
  'failed to get invoices': '获取发票列表失败',
  'failed to record payment': '记录支付失败',
  'failed to approve payment': '审核支付失败',
  'failed to mark invoice as paid': '标记发票已付失败',
  'failed to upload payment proof': '上传支付凭证失败',
  'payment approved! receipt sent to client.': '支付已审核！收据已发送给客户。',
  'payment recorded! awaiting admin approval.': '支付已记录！等待管理员审核。',
  'payment method not found': '未找到支付方式',
  'failed to update payment instructions': '更新支付说明失败',
  'failed to get payment instructions': '获取支付说明失败',
  'payment instructions saved successfully': '支付说明保存成功',
  'payment instructions saved successfully!': '支付说明保存成功！',
  'payment instructions updated successfully': '支付说明更新成功',
  'payment instructions updated successfully!': '支付说明更新成功！',

  // PayPal
  'paypal not configured': 'PayPal 未配置',
  'paypal credentials not configured': 'PayPal 凭据未配置',
  'paypal connection successful': 'PayPal 连接成功',
  'paypal connection successful!': 'PayPal 连接成功！',
  'paypal settings saved successfully': 'PayPal 设置保存成功',
  'paypal settings saved successfully!': 'PayPal 设置保存成功！',
  'failed to update paypal settings': '更新 PayPal 设置失败',
  'failed to test paypal connection': '测试 PayPal 连接失败',
  'failed to get paypal settings': '获取 PayPal 设置失败',

  // Messages / conversations
  'failed to send message': '发送消息失败',
  'failed to load messages': '加载消息失败',
  'failed to load conversations': '加载会话失败',
  'failed to start conversation. please try again.': '开启会话失败。请重试。',

  // Schedules / tasks / notifications
  'status updated successfully': '状态更新成功',
  'schedule created successfully': '日程创建成功',
  'schedule updated successfully': '日程更新成功',
  'schedule deleted': '日程已删除',
  'error updating status': '更新状态失败',
  'error deleting schedule': '删除日程失败',
  'failed to send reminder': '发送提醒失败',
  'are you sure you want to delete this schedule?': '确定要删除此日程吗？',
  'delete this event?': '删除此活动？',
  'task created successfully': '任务创建成功',
  'task updated successfully': '任务更新成功',
  'task status updated': '任务状态已更新',
  'task deleted': '任务已删除',
  'tasks marked as complete': '任务已标记为完成',
  'are you sure you want to delete this task?': '确定要删除此任务吗？',
  'notification deleted': '通知已删除',
  'all notifications marked as read': '所有通知已标记为已读',
  'failed to mark notification as read': '标记通知为已读失败',

  // Users / roles / coaches
  'user created successfully': '用户创建成功',
  'user updated successfully': '用户更新成功',
  'user status updated': '用户状态已更新',
  'user deleted': '用户已删除',
  'user not found': '未找到用户',
  'email already exists': '邮箱已存在',
  'cannot delete yourself': '不能删除自己',
  'failed to load users': '加载用户失败',
  'failed to delete user': '删除用户失败',
  'failed to update user': '更新用户失败',
  'failed to get users': '获取用户列表失败',
  'failed to get user': '获取用户失败',
  'are you sure you want to delete this user?': '确定要删除此用户吗？',
  'role not found': '未找到角色',
  'system roles cannot be deleted': '系统角色不能删除',
  'cannot delete system role': '不能删除系统角色',
  'cannot delete role with assigned users': '不能删除有关联用户的角色',
  'cannot change system role name': '不能修改系统角色名称',
  'failed to get roles': '获取角色列表失败',
  'failed to get role': '获取角色失败',
  'failed to get permissions': '获取权限失败',
  'failed to update role': '更新角色失败',
  'failed to create role': '创建角色失败',
  'failed to delete role': '删除角色失败',
  'are you sure you want to delete this role?': '确定要删除此角色吗？',
  'this user is already a coach': '该用户已经是教练',
  'this profile is already assigned to this coach': '该档案已分配给此教练',
  'coach has reached maximum client capacity': '该教练已达到最大客户容量',
  'failed to get coaches': '获取教练列表失败',
  'failed to update coach': '更新教练失败',
  'failed to remove coach': '移除教练失败',
  'failed to create coach': '创建教练失败',
  'failed to remove assignment': '移除分配失败',
  'failed to create assignment': '创建分配失败',
  'failed to get assignments': '获取分配列表失败',
  'are you sure you want to remove this coach?': '确定要移除此教练吗？',
  'are you sure you want to remove this assignment?': '确定要移除此分配吗？',
  'please select a coach': '请选择教练',

  // Custom fields / plans / categories
  'a field with this name already exists': '已存在同名字段',
  'a category with this name already exists': '已存在同名分类',
  'please provide options for this field type': '请为此字段类型提供选项',
  'please add at least one option for this field type': '请为此字段类型至少添加一个选项',
  'please fill in field name and label': '请填写字段名称和标签',
  'failed to get custom fields': '获取自定义字段失败',
  'failed to update custom field': '更新自定义字段失败',
  'failed to create custom field': '创建自定义字段失败',
  'failed to delete custom field': '删除自定义字段失败',
  'are you sure you want to delete this custom field?': '确定要删除此自定义字段吗？',
  'plan not found': '未找到套餐',
  'plan created successfully': '套餐创建成功',
  'plan created successfully!': '套餐创建成功！',
  'plan updated successfully': '套餐更新成功',
  'plan updated successfully!': '套餐更新成功！',
  'plan deleted': '套餐已删除',
  'cannot delete plan with active subscribers': '不能删除有活跃订阅者的套餐',
  'failed to get client plans': '获取客户套餐失败',
  'failed to get client plan': '获取客户套餐失败',
  'failed to update client plan': '更新客户套餐失败',
  'failed to create client plan': '创建客户套餐失败',
  'failed to delete client plan': '删除客户套餐失败',
  'failed to toggle plan status': '切换套餐状态失败',

  // Affiliates
  'affiliate not found': '未找到联盟成员',
  'commission not found': '未找到佣金记录',
  'payout not found': '未找到结算记录',
  'cannot delete processed payout': '不能删除已处理的结算',
  'failed to get affiliates': '获取联盟列表失败',
  'failed to get affiliate': '获取联盟成员失败',
  'failed to update affiliate': '更新联盟成员失败',
  'failed to auto upgrade affiliates': '自动升级联盟等级失败',
  'failed to get commissions': '获取佣金列表失败',
  'failed to update commission': '更新佣金失败',
  'failed to create commission': '创建佣金失败',
  'failed to delete commission': '删除佣金失败',
  'failed to get payouts': '获取结算列表失败',
  'failed to get payout': '获取结算失败',
  'failed to get payout settings': '获取结算设置失败',
  'failed to update payout': '更新结算失败',
  'failed to update payout settings': '更新结算设置失败',
  'failed to create payout': '创建结算失败',
  'failed to delete payout': '删除结算失败',
  'failed to get tiers': '获取等级列表失败',
  'failed to create tier': '创建等级失败',
  'failed to update tier': '更新等级失败',
  'failed to add points': '添加积分失败',

  // Trends / activity logs
  'trend not found': '未找到趋势',
  'failed to update trend': '更新趋势失败',
  'failed to update trend settings': '更新趋势设置失败',
  'failed to update trend category': '更新趋势分类失败',
  'delete this trend?': '删除此趋势？',
  'delete this category?': '删除此分类？',
  'failed to get activity logs': '获取活动日志失败',
  'failed to get dashboard stats': '获取仪表板数据失败',

  // Misc
  'no updates provided': '未提供更新内容',
  'no file uploaded': '未上传文件',
  'connection failed': '连接失败',
  'connection saved successfully': '连接保存成功',
  'mapping saved successfully': '映射保存成功',
  'please enter a connection name': '请输入连接名称',
  'please fill in host and database name': '请填写主机和数据库名称',
  'please select tables and create at least one field mapping': '请选择表并至少创建一个字段映射',
  'are you sure you want to delete this connection?': '确定要删除此连接吗？',
  'are you sure you want to delete this page?': '确定要删除此页吗？',
  'are you sure you want to delete this file?': '确定要删除此文件吗？',
  'delete this key?': '删除此密钥？',
  'subscription created successfully': '订阅创建成功',
  'subscription created successfully!': '订阅创建成功！',
  'reset to default layout': '已重置为默认布局',
  'layout saved successfully': '布局保存成功',
  'layout saved successfully!': '布局保存成功！',
};

// Heuristic prefix rewriters — applied AFTER direct lookup misses.
const PREFIX_RULES = [
  [/^failed to /i,  '失败：'],
  [/^error: /i,     '错误：'],
  [/^please /i,     '请'],
  [/^are you sure/i,'确定'],
];

// ───────────────────────────────────────────────────────────────
//  Core translator
// ───────────────────────────────────────────────────────────────

const isZhActive = () => {
  try {
    return (localStorage.getItem('language') || 'zh') === 'zh';
  } catch { return true; }
};

const normalise = (s) => String(s || '').trim().toLowerCase();

const translate = (msg) => {
  if (!isZhActive()) return msg;
  if (typeof msg !== 'string' || !msg) return msg;

  // Already contains CJK characters → assume already-translated, pass through.
  if (/[一-鿿]/.test(msg)) return msg;

  // Strip trailing punctuation for lookup, then restore.
  const trailingMatch = msg.match(/[\s.!?。！？]+$/);
  const stripped = trailingMatch ? msg.slice(0, msg.length - trailingMatch[0].length) : msg;
  const trailer = trailingMatch ? trailingMatch[0] : '';

  const hit = DICT[normalise(stripped)];
  if (hit) return hit + trailer.replace(/[.!?]/g, m => ({ '.': '。', '!': '！', '?': '？' }[m] || m));

  // Whole-string heuristic rewrite (covers "Failed to do xyz" style messages
  // not in the dictionary yet).
  for (const [re, zh] of PREFIX_RULES) {
    if (re.test(msg)) return msg.replace(re, zh);
  }

  return msg; // Unknown — leave as-is so the user still sees *something*.
};

// ───────────────────────────────────────────────────────────────
//  Install patches
// ───────────────────────────────────────────────────────────────

let installed = false;
export function installGlobalI18n() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // 1) toast.* — patch in place (all imports share the same singleton).
  ['success', 'error', 'info', 'warning', 'warn', 'dark', 'default'].forEach(method => {
    const original = toast[method];
    if (typeof original === 'function') {
      toast[method] = (content, options) => original(translate(content), options);
    }
  });
  // toast() itself is callable
  if (typeof toast === 'function') {
    const originalToast = toast.bind(null);
    // Can't rebind the imported binding, but toast._callFn style is internal.
    // We only patch the explicit methods above; most code uses toast.success/error.
    void originalToast;
  }

  // 2) window.alert / confirm / prompt — patch globals.
  const origAlert = window.alert.bind(window);
  window.alert = (msg) => origAlert(translate(msg));

  const origConfirm = window.confirm.bind(window);
  window.confirm = (msg) => origConfirm(translate(msg));

  const origPrompt = window.prompt.bind(window);
  window.prompt = (msg, def) => origPrompt(translate(msg), def);
}

// Exported for tests / manual use from other utilities.
export const translateMessage = translate;
